// pages/UsersPage.tsx
import React, { useEffect, useState, useMemo } from 'react';
import {
  Button,
  Box,
  Modal,
  TextField,
  Select,
  MenuItem,
  Grid,
  Card,
  CardContent,
  Typography,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  useMediaQuery,
  useTheme,
  Alert,
  CircularProgress,
  FormControl,
  InputLabel,
  alpha,
  Checkbox,
  FormControlLabel,
  Paper,
  TablePagination,
  Tooltip,
  Menu,
  ListItemIcon,
  ListItemText,
  Divider,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  CreditCard as CreditCardIcon,
  Person as PersonIcon,
  AdminPanelSettings as AdminIcon,
  Store as StoreIcon,
  Download as DownloadIcon,
  ViewColumn as ViewColumnIcon,
  CheckBox as CheckBoxIcon,
  Schedule as ScheduleIcon,
  Login as LoginIcon
} from '@mui/icons-material';
import { addUser, deleteUser, fetchUsers, updateUser, fetchLocations } from '../api/superAdmin';
import { fetchWholesaleTiers, WholesaleTier } from '../api/wholesaleApi';
import UserDetailsModal, { STORE_CREDIT_TAB } from '../components/UserDetailsModal';
import UserFilter, { FilterState } from '../components/UserFilter';
import UserStats from '../components/UserStats';
import { User } from '../types/user';
import { formatPhoneInput } from '../utils/phone';

// Main Users Page Component
const UsersPage: React.FC = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [filteredUsers, setFilteredUsers] = useState<User[]>([]);
  const [locations, setLocations] = useState<Array<{ id: number; name: string }>>([]);
  const [tiers, setTiers] = useState<WholesaleTier[]>([]);
  const [openModal, setOpenModal] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [detailsInitialTab, setDetailsInitialTab] = useState(0);
  const [userToDelete, setUserToDelete] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [form, setForm] = useState<Partial<User>>({
    email: '',
    password: '',
    role: 'customer',
    name: '',
    phone: '',
    location_id: null,
  });
  const [selectedUser, setSelectedUser] = useState<number | null>(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [currentFilters, setCurrentFilters] = useState<FilterState>({
    search: '',
    location_id: 'all',
    userType: 'all'
  });

  // New state variables
  const [selectedUsers, setSelectedUsers] = useState<number[]>([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [columnMenuAnchor, setColumnMenuAnchor] = useState<null | HTMLElement>(null);
  const [visibleColumns, setVisibleColumns] = useState({
    name: true,
    email: true,
    phone: true,
    role: true,
    location: true,
    joined: true,
    lastLogin: true,
    status: true
  });
  const [bulkDeleteDialog, setBulkDeleteDialog] = useState(false);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const loadUsers = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await fetchUsers();
      const userArray = Array.isArray(data) ? data : [];
      setUsers(userArray);
      setFilteredUsers(userArray);
      setSelectedUsers([]); // Clear selections when data reloads
    } catch (err) {
      console.error('Error fetching users:', err);
      setError('Failed to load users. Please try again.');
      setUsers([]);
      setFilteredUsers([]);
    } finally {
      setLoading(false);
    }
  };

  const loadLocations = async () => {
    try {
      const data = await fetchLocations();
      setLocations(data.locations || []);
    } catch (err) {
      console.error('Error fetching locations:', err);
    }
  };

  const loadTiers = async () => {
    try {
      setTiers(await fetchWholesaleTiers());
    } catch (err) {
      console.error('Error fetching wholesale tiers:', err);
    }
  };

  const defaultTierId = useMemo(
    () => tiers.find((t) => t.is_default)?.id ?? tiers[0]?.id ?? null,
    [tiers]
  );

  // Get user status based on last_login
  const getUserStatus = (user: User): 'active' | 'inactive' | 'new' => {
    if (!user.last_login) return 'new';
    const lastLogin = new Date(user.last_login);
    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
    return lastLogin >= thirtyDaysAgo ? 'active' : 'inactive';
  };

  const formatLastLogin = (lastLogin?: string | null) => {
    if (!lastLogin) return 'Never';
    try {
      const date = new Date(lastLogin);
      const now = new Date();
      const diffDays = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60 * 24));

      if (diffDays === 0) return 'Today';
      if (diffDays === 1) return 'Yesterday';
      if (diffDays < 7) return `${diffDays} days ago`;
      return date.toLocaleDateString();
    } catch {
      return 'Unknown';
    }
  };

  const handleFilterChange = (filters: FilterState) => {
    setCurrentFilters(filters);
    setPage(0); // Reset to first page when filtering

    let filtered = [...users];

    // Apply search filter
    if (filters.search) {
      const searchLower = filters.search.toLowerCase();
      filtered = filtered.filter(user =>
        user.name?.toLowerCase().includes(searchLower) ||
        user.email?.toLowerCase().includes(searchLower) ||
        user.phone?.toLowerCase().includes(searchLower)
      );
    }

    // Apply user type filter
    if (filters.userType !== 'all') {
      if (filters.userType === 'customer') {
        filtered = filtered.filter(user => user.role === 'customer');
      } else if (filters.userType === 'staff') {
        filtered = filtered.filter(user =>
          user.role === 'super_admin' || user.role === 'location_admin'
        );
      } else if (filters.userType === 'wholesale') {
        filtered = filtered.filter(user => !!user.is_wholesale);
      }
    }

    // Apply location filter
    if (filters.location_id !== 'all') {
      filtered = filtered.filter(user =>
        user.location_id?.toString() === filters.location_id
      );
    }

    setFilteredUsers(filtered);
  };

  // Quick filter handlers
  const handleQuickFilter = (type: string) => {
    let newFilters = { ...currentFilters };

    switch (type) {
      case 'all':
        newFilters.userType = 'all';
        newFilters.location_id = 'all';
        setCurrentFilters(newFilters);
        handleFilterChange(newFilters);
        break;
      case 'customer':
        newFilters.userType = 'customer';
        setCurrentFilters(newFilters);
        handleFilterChange(newFilters);
        break;
      case 'staff':
        newFilters.userType = 'staff';
        setCurrentFilters(newFilters);
        handleFilterChange(newFilters);
        break;
      case 'wholesale':
        newFilters.userType = 'wholesale';
        setCurrentFilters(newFilters);
        handleFilterChange(newFilters);
        break;
      case 'no-location':
        newFilters.location_id = 'none';
        setCurrentFilters(newFilters);
        handleFilterChange(newFilters);
        break;
      case 'active':
        // Custom filter for active users
        const activeUsers = users.filter(u => getUserStatus(u) === 'active');
        setFilteredUsers(activeUsers);
        break;
    }
  };

  const handleSubmit = async () => {
    try {
      setError(null);
      setSuccess(null);

      if (form.id) {
        // Editing: password is optional ("leave blank to keep current").
        await updateUser(form.id, {
          email: form.email || '',
          ...(form.password ? { password: form.password } : {}),
          role: form.role || 'customer',
          name: form.name || '',
          phone: form.phone || '',
          location_id: form.location_id || null,
          is_wholesale: !!form.is_wholesale,
          wholesale_tier_id: form.is_wholesale ? (form.wholesale_tier_id ?? null) : null,
        });
        setSuccess('User updated successfully.');
      } else {
        // Creating. Customers are created without a password (the backend emails
        // them a secure link to set it). Non-customer/admin roles require a
        // password, which is set directly — no setup email is sent.
        const role = form.role || 'customer';
        const isCustomer = role === 'customer';

        const result = await addUser({
          email: form.email || '',
          ...(isCustomer ? {} : { password: form.password || '' }),
          role,
          name: form.name || '',
          phone: form.phone || '',
          location_id: form.location_id || null,
          is_wholesale: !!form.is_wholesale,
          wholesale_tier_id: form.is_wholesale ? (form.wholesale_tier_id ?? null) : null,
        });

        if (!isCustomer) {
          setSuccess('User created successfully.');
        } else if (result?.setupEmailSent === false) {
          setSuccess(
            `User created, but the setup email could not be sent to ${form.email}. ` +
            `Ask the customer to use "Forgot password" to receive a new link.`
          );
        } else {
          setSuccess(`User created. A password setup link has been emailed to ${form.email}.`);
        }
      }

      setOpenModal(false);
      resetForm();
      loadUsers();
    } catch (err) {
      console.error('Error submitting user:', err);
      setError('Failed to save user. Please try again.');
    }
  };

  const handleDelete = async (id: number) => {
    try {
      setError(null);
      await deleteUser(id);
      loadUsers();
      setDeleteDialogOpen(false);
    } catch (err) {
      console.error('Error deleting user:', err);
      setError('Failed to delete user. Please try again.');
    }
  };

  const handleBulkDelete = async () => {
    try {
      setError(null);
      await Promise.all(selectedUsers.map(id => deleteUser(id)));
      setBulkDeleteDialog(false);
      setSelectedUsers([]);
      loadUsers();
    } catch (err) {
      console.error('Error deleting users:', err);
      setError('Failed to delete some users. Please try again.');
    }
  };

  const resetForm = () => {
    setForm({
      email: '',
      password: '',
      role: 'customer',
      name: '',
      phone: '',
      location_id: null,
    });
  };

  const handleAddUserClick = () => {
    resetForm();
    setOpenModal(true);
  };

  const handleEditClick = (user: User, event: React.MouseEvent) => {
    event.stopPropagation();
    setForm({
      id: user.id,
      email: user.email,
      password: '',
      role: user.role,
      name: user.name,
      phone: user.phone || '',
      location_id: user.location_id,
      is_wholesale: !!user.is_wholesale,
      wholesale_tier_id: user.wholesale_tier_id ?? null,
    });
    setOpenModal(true);
  };

  const openDeleteDialog = (id: number, event: React.MouseEvent) => {
    event.stopPropagation();
    setUserToDelete(id);
    setDeleteDialogOpen(true);
  };

  const getRoleIcon = (role: string) => {
    switch (role) {
      case 'super_admin': return <AdminIcon color="error" />;
      case 'location_admin': return <StoreIcon color="primary" />;
      default: return <PersonIcon color="action" />;
    }
  };

  const getRoleColor = (role: string) => {
    switch (role) {
      case 'super_admin': return 'error';
      case 'location_admin': return 'primary';
      default: return 'default';
    }
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleDateString();
    } catch {
      return 'Invalid';
    }
  };

  // Export functionality
  const exportToCSV = () => {
    const headers = ['Name', 'Email', 'Phone', 'Role', 'Location', 'Joined', 'Last Login', 'Status'];
    const csvData = filteredUsers.map(user => [
      user.name,
      user.email,
      user.phone || '',
      user.role,
      user.location_name || '',
      formatDate(user.created_at),
      formatLastLogin(user.last_login),
      getUserStatus(user)
    ]);

    const csv = [headers, ...csvData].map(row => row.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `users_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
  };

  // Selection handlers - FIXED VERSION
  const handleSelectAll = (event: React.ChangeEvent<HTMLInputElement>) => {
    const checked = event.target.checked;

    if (checked) {
      // Add all current page users to selection (avoid duplicates)
      const newSelectedIds = [
        ...selectedUsers,
        ...paginatedUsers
          .map(u => u.id)
          .filter(id => !selectedUsers.includes(id))
      ];
      setSelectedUsers(newSelectedIds);
    } else {
      // Remove all current page users from selection
      const currentPageIds = paginatedUsers.map(u => u.id);
      const newSelectedIds = selectedUsers.filter(id => !currentPageIds.includes(id));
      setSelectedUsers(newSelectedIds);
    }
  };

  const handleSelectOne = (id: number, event: React.ChangeEvent<HTMLInputElement>) => {
    event.stopPropagation();
    const checked = event.target.checked;

    if (checked) {
      setSelectedUsers([...selectedUsers, id]);
    } else {
      setSelectedUsers(selectedUsers.filter(uid => uid !== id));
    }
  };



  // Pagination
  const paginatedUsers = useMemo(() => {
    const start = page * rowsPerPage;
    return filteredUsers.slice(start, start + rowsPerPage);
  }, [filteredUsers, page, rowsPerPage]);
  // Calculate selection states for current page
  const paginatedUserIds = paginatedUsers.map(u => u.id);
  const selectedOnPage = selectedUsers.filter(id => paginatedUserIds.includes(id));
  const allSelectedOnPage = paginatedUsers.length > 0 && selectedOnPage.length === paginatedUsers.length;
  const indeterminateSelected = selectedOnPage.length > 0 && selectedOnPage.length < paginatedUsers.length;

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Column visibility menu
  const handleColumnMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setColumnMenuAnchor(event.currentTarget);
  };

  const handleColumnMenuClose = () => {
    setColumnMenuAnchor(null);
  };

  const toggleColumn = (column: keyof typeof visibleColumns) => {
    setVisibleColumns({
      ...visibleColumns,
      [column]: !visibleColumns[column]
    });
  };

  useEffect(() => {
    loadUsers();
    loadLocations();
    loadTiers();
  }, []);

  return (
    <Box sx={{ p: isMobile ? 1 : 3 }}>
      {/* Header with actions */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h5" fontWeight="bold" color="primary">
          User Management
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Tooltip title="Export to CSV">
            <Button
              variant="outlined"
              startIcon={<DownloadIcon />}
              onClick={exportToCSV}
              size="small"
              sx={{ borderRadius: 2 }}
            >
              Export
            </Button>
          </Tooltip>
          <Tooltip title="Add new user">
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleAddUserClick}
              size="small"
              sx={{ borderRadius: 2 }}
            >
              Add User
            </Button>
          </Tooltip>
        </Box>
      </Box>

      {/* Summary Stats */}
      <UserStats users={users} />

      {/* Quick Filter Chips */}
      <Box sx={{ display: 'flex', gap: 0.5, mb: 2, flexWrap: 'wrap' }}>
        <Chip
          label="All Users"
          onClick={() => handleQuickFilter('all')}
          color={currentFilters.userType === 'all' && currentFilters.location_id === 'all' ? 'primary' : 'default'}
          size="small"
          variant={currentFilters.userType === 'all' && currentFilters.location_id === 'all' ? 'filled' : 'outlined'}
        />
        <Chip
          label="Customers"
          onClick={() => handleQuickFilter('customer')}
          color={currentFilters.userType === 'customer' ? 'primary' : 'default'}
          size="small"
          variant={currentFilters.userType === 'customer' ? 'filled' : 'outlined'}
        />
        <Chip
          label="Staff"
          onClick={() => handleQuickFilter('staff')}
          color={currentFilters.userType === 'staff' ? 'primary' : 'default'}
          size="small"
          variant={currentFilters.userType === 'staff' ? 'filled' : 'outlined'}
        />
        <Chip
          label="Wholesale"
          onClick={() => handleQuickFilter('wholesale')}
          size="small"
          variant={currentFilters.userType === 'wholesale' ? 'filled' : 'outlined'}
          sx={currentFilters.userType === 'wholesale'
            ? { bgcolor: '#7C3AED', color: '#fff', fontWeight: 700, '&:hover': { bgcolor: '#6D28D9' } }
            : { color: '#7C3AED', borderColor: '#7C3AED' }}
        />
        <Chip
          label="No Location"
          onClick={() => handleQuickFilter('no-location')}
          color={currentFilters.location_id === 'none' ? 'primary' : 'default'}
          size="small"
          variant={currentFilters.location_id === 'none' ? 'filled' : 'outlined'}
        />
        <Chip
          icon={<ScheduleIcon />}
          label="Active (30d)"
          onClick={() => handleQuickFilter('active')}
          color="success"
          size="small"
          variant="outlined"
        />
      </Box>

      {/* Filters */}
      <UserFilter onFilterChange={handleFilterChange} locations={locations} />

      {/* Bulk Actions Bar */}
      {selectedUsers.length > 0 && (
        <Paper
          sx={{
            p: 1,
            mb: 2,
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            bgcolor: alpha(theme.palette.primary.main, 0.05),
            border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
            borderRadius: 2
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CheckBoxIcon color="primary" />
            <Typography variant="body2" fontWeight="medium">
              {selectedUsers.length} selected
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 1, ml: 'auto' }}>
            <Tooltip title="Delete selected users">
              <Button
                size="small"
                startIcon={<DeleteIcon />}
                color="error"
                variant="contained"
                onClick={() => setBulkDeleteDialog(true)}
              >
                Delete ({selectedUsers.length})
              </Button>
            </Tooltip>
          </Box>
        </Paper>
      )}

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Success Alert */}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      {/* Loading State */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : filteredUsers.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 6 }}>
          <PersonIcon sx={{ fontSize: 60, color: theme.palette.grey[300], mb: 2 }} />
          <Typography variant="h6" color="text.secondary" gutterBottom>
            No users found
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            {currentFilters.search || currentFilters.userType !== 'all' || currentFilters.location_id !== 'all'
              ? "Try adjusting your filters"
              : "Get started by adding your first user"}
          </Typography>
          {!currentFilters.search && currentFilters.userType === 'all' && currentFilters.location_id === 'all' && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleAddUserClick}
            >
              Add Your First User
            </Button>
          )}
        </Box>
      ) : isMobile ? (
        // Mobile Card View
        <Grid container spacing={2}>
          {paginatedUsers.map((user) => (
            <Grid item xs={12} key={user.id}>
              <Card sx={{ borderRadius: 2, position: 'relative' }}>
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Checkbox
                        edge="start"
                        checked={selectedUsers.includes(user.id)}
                        onChange={(e) => handleSelectOne(user.id, e)}
                        onClick={(e) => e.stopPropagation()}
                        size="small"
                      />
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        {getRoleIcon(user.role)}
                        <Typography variant="subtitle1" sx={{ ml: 1, fontWeight: 'medium' }}>
                          {user.name}
                        </Typography>
                      </Box>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                      <Chip
                        label={user.role.replace('_', ' ')}
                        size="small"
                        color={getRoleColor(user.role) as any}
                        variant="outlined"
                      />
                      {user.is_wholesale && (
                        <Chip
                          label={user.wholesale_tier_name ? `Wholesale · ${user.wholesale_tier_name}` : 'Wholesale'}
                          size="small"
                          sx={{ bgcolor: '#7C3AED', color: '#fff', fontWeight: 700 }}
                        />
                      )}
                    </Box>
                  </Box>

                  <Box sx={{ ml: 4 }}>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                      {user.email}
                    </Typography>

                    {user.phone && (
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                        {user.phone}
                      </Typography>
                    )}

                    <Typography variant="caption" sx={{ display: 'block', mb: 0.5 }}>
                      {user.location_name || 'No location assigned'}
                    </Typography>

                    <Box sx={{ display: 'flex', gap: 2, mb: 1 }}>
                      <Typography variant="caption" color="text.secondary">
                        Joined: {formatDate(user.created_at)}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <LoginIcon sx={{ fontSize: 12 }} />
                        Last: {formatLastLogin(user.last_login)}
                      </Typography>
                    </Box>

                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                      <Chip
                        label={getUserStatus(user)}
                        size="small"
                        color={getUserStatus(user) === 'active' ? 'success' : getUserStatus(user) === 'new' ? 'info' : 'default'}
                        sx={{ height: 20, fontSize: '0.7rem' }}
                      />
                    </Box>
                  </Box>

                  <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 0.5, mt: 1 }}>
                    <IconButton
                      size="small"
                      color="primary"
                      onClick={() => {
                        setSelectedUser(user.id);
                        setDetailsInitialTab(0);
                        setDetailsOpen(true);
                      }}
                    >
                      <PersonIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      color="primary"
                      onClick={(e) => handleEditClick(user, e)}
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                    {user.role === 'customer' && (
                      <Tooltip title="Store credit">
                        <IconButton
                          size="small"
                          color="secondary"
                          onClick={(e) => { e.stopPropagation(); setSelectedUser(user.id); setDetailsInitialTab(STORE_CREDIT_TAB); setDetailsOpen(true); }}
                        >
                          <CreditCardIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                    <IconButton
                      size="small"
                      color="error"
                      onClick={(e) => openDeleteDialog(user.id, e)}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      ) : (
        // Desktop Table View
        <>
          <Table size="small" sx={{ mb: 2 }}>
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox">
                  <Checkbox
                    indeterminate={indeterminateSelected}
                    checked={allSelectedOnPage}
                    onChange={handleSelectAll}
                    size="small"
                  />
                </TableCell>
                {visibleColumns.name && <TableCell>Name</TableCell>}
                {visibleColumns.email && <TableCell>Email</TableCell>}
                {visibleColumns.phone && <TableCell>Phone</TableCell>}
                {visibleColumns.role && <TableCell>Role</TableCell>}
                {visibleColumns.location && <TableCell>Location</TableCell>}
                {visibleColumns.joined && <TableCell>Joined</TableCell>}
                {visibleColumns.lastLogin && <TableCell>Last Login</TableCell>}
                {visibleColumns.status && <TableCell>Status</TableCell>}
                <TableCell align="right">
                  <IconButton size="small" onClick={handleColumnMenuOpen}>
                    <ViewColumnIcon fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedUsers.map((user) => (
                <TableRow
                  key={user.id}
                  hover
                  onClick={() => {
                    setSelectedUser(user.id);
                    setDetailsInitialTab(0);
                    setDetailsOpen(true);
                  }}
                  sx={{ cursor: 'pointer' }}
                  selected={selectedUsers.includes(user.id)}
                >
                  <TableCell padding="checkbox" onClick={(e) => e.stopPropagation()}>
                    <Checkbox
                      checked={selectedUsers.includes(user.id)}
                      onChange={(e) => handleSelectOne(user.id, e)}
                      size="small"
                    />
                  </TableCell>
                  {visibleColumns.name && (
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        {getRoleIcon(user.role)}
                        <Typography sx={{ ml: 1, fontSize: '0.875rem' }}>{user.name}</Typography>
                        {user.is_wholesale && (
                          <Chip
                            label={user.wholesale_tier_name ? `Wholesale · ${user.wholesale_tier_name}` : 'Wholesale'}
                            size="small"
                            sx={{ ml: 1, height: 20, bgcolor: '#7C3AED', color: '#fff', fontWeight: 700, '& .MuiChip-label': { px: 1 } }}
                          />
                        )}
                      </Box>
                    </TableCell>
                  )}
                  {visibleColumns.email && <TableCell sx={{ fontSize: '0.875rem' }}>{user.email}</TableCell>}
                  {visibleColumns.phone && <TableCell sx={{ fontSize: '0.875rem' }}>{user.phone || '-'}</TableCell>}
                  {visibleColumns.role && (
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                        <Chip
                          label={user.role.replace('_', ' ')}
                          size="small"
                          color={getRoleColor(user.role) as any}
                          variant="outlined"
                          sx={{ height: 24 }}
                        />
                      </Box>
                    </TableCell>
                  )}
                  {visibleColumns.location && <TableCell sx={{ fontSize: '0.875rem' }}>{user.location_name || 'N/A'}</TableCell>}
                  {visibleColumns.joined && <TableCell sx={{ fontSize: '0.875rem' }}>{formatDate(user.created_at)}</TableCell>}
                  {visibleColumns.lastLogin && (
                    <TableCell>
                      <Tooltip title={user.last_login ? new Date(user.last_login).toLocaleString() : 'Never logged in'}>
                        <Typography sx={{ fontSize: '0.875rem', display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <LoginIcon sx={{ fontSize: 14, color: theme.palette.text.secondary }} />
                          {formatLastLogin(user.last_login)}
                        </Typography>
                      </Tooltip>
                    </TableCell>
                  )}
                  {visibleColumns.status && (
                    <TableCell>
                      <Chip
                        label={getUserStatus(user)}
                        size="small"
                        color={getUserStatus(user) === 'active' ? 'success' : getUserStatus(user) === 'new' ? 'info' : 'default'}
                        sx={{ height: 20, fontSize: '0.7rem' }}
                      />
                    </TableCell>
                  )}
                  <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                    <IconButton
                      size="small"
                      color="primary"
                      onClick={(e) => handleEditClick(user, e)}
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                    {user.role === 'customer' && (
                      <Tooltip title="Store credit">
                        <IconButton
                          size="small"
                          color="secondary"
                          onClick={(e) => { e.stopPropagation(); setSelectedUser(user.id); setDetailsInitialTab(STORE_CREDIT_TAB); setDetailsOpen(true); }}
                        >
                          <CreditCardIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                    <IconButton
                      size="small"
                      color="error"
                      onClick={(e) => openDeleteDialog(user.id, e)}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>

          <TablePagination
            component="div"
            count={filteredUsers.length}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={[10, 25, 50, 100]}
          />
        </>
      )}

      {/* Column Visibility Menu */}
      <Menu
        anchorEl={columnMenuAnchor}
        open={Boolean(columnMenuAnchor)}
        onClose={handleColumnMenuClose}
      >
        <MenuItem dense onClick={() => toggleColumn('name')}>
          <ListItemIcon>
            <Checkbox checked={visibleColumns.name} size="small" />
          </ListItemIcon>
          <ListItemText>Name</ListItemText>
        </MenuItem>
        <MenuItem dense onClick={() => toggleColumn('email')}>
          <ListItemIcon>
            <Checkbox checked={visibleColumns.email} size="small" />
          </ListItemIcon>
          <ListItemText>Email</ListItemText>
        </MenuItem>
        <MenuItem dense onClick={() => toggleColumn('phone')}>
          <ListItemIcon>
            <Checkbox checked={visibleColumns.phone} size="small" />
          </ListItemIcon>
          <ListItemText>Phone</ListItemText>
        </MenuItem>
        <MenuItem dense onClick={() => toggleColumn('role')}>
          <ListItemIcon>
            <Checkbox checked={visibleColumns.role} size="small" />
          </ListItemIcon>
          <ListItemText>Role</ListItemText>
        </MenuItem>
        <MenuItem dense onClick={() => toggleColumn('location')}>
          <ListItemIcon>
            <Checkbox checked={visibleColumns.location} size="small" />
          </ListItemIcon>
          <ListItemText>Location</ListItemText>
        </MenuItem>
        <MenuItem dense onClick={() => toggleColumn('joined')}>
          <ListItemIcon>
            <Checkbox checked={visibleColumns.joined} size="small" />
          </ListItemIcon>
          <ListItemText>Joined</ListItemText>
        </MenuItem>
        <MenuItem dense onClick={() => toggleColumn('lastLogin')}>
          <ListItemIcon>
            <Checkbox checked={visibleColumns.lastLogin} size="small" />
          </ListItemIcon>
          <ListItemText>Last Login</ListItemText>
        </MenuItem>
        <MenuItem dense onClick={() => toggleColumn('status')}>
          <ListItemIcon>
            <Checkbox checked={visibleColumns.status} size="small" />
          </ListItemIcon>
          <ListItemText>Status</ListItemText>
        </MenuItem>
        <Divider />
        <MenuItem dense onClick={() => setVisibleColumns({
          name: true, email: true, phone: true, role: true,
          location: true, joined: true, lastLogin: true, status: true
        })}>
          <ListItemText>Reset to Default</ListItemText>
        </MenuItem>
      </Menu>

      <UserDetailsModal
        userId={selectedUser}
        open={detailsOpen}
        initialTab={detailsInitialTab}
        onClose={() => setDetailsOpen(false)}
      />

      {/* Add/Edit User Modal */}
      <Modal open={openModal} onClose={() => setOpenModal(false)}>
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: isMobile ? '90%' : 400,
            bgcolor: 'background.paper',
            boxShadow: 24,
            p: 3,
            borderRadius: 2,
            maxHeight: '90vh',
            overflow: 'auto'
          }}
        >
          <Typography variant="h6" gutterBottom>
            {form.id ? 'Edit User' : 'Add User'}
          </Typography>

          <TextField
            label="Name"
            value={form.name || ''}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            fullWidth
            margin="dense"
            size="small"
            required
          />
          <TextField
            label="Email"
            type="email"
            value={form.email || ''}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
            fullWidth
            margin="dense"
            size="small"
            required
          />
          {form.id ? (
            // Editing an existing user: allow an optional password reset.
            <TextField
              label="Password"
              type="password"
              value={form.password || ''}
              onChange={(e) => setForm({ ...form, password: e.target.value })}
              fullWidth
              margin="dense"
              size="small"
              helperText="Leave blank to keep current password"
            />
          ) : (form.role || 'customer') === 'customer' ? (
            // Creating a customer: no password is collected here. The customer
            // receives an email with a secure link to create their own password.
            <Alert severity="info" sx={{ mt: 1, mb: 0.5 }}>
              The customer will receive an email with a secure link to create their password.
            </Alert>
          ) : (
            // Creating a non-customer (admin) role: a password is required and
            // is set directly. No setup email is sent for internal/admin roles.
            <TextField
              label="Password"
              type="password"
              value={form.password || ''}
              onChange={(e) => setForm({ ...form, password: e.target.value })}
              fullWidth
              margin="dense"
              size="small"
              required
            />
          )}
          <TextField
            label="Phone"
            type="tel"
            inputProps={{ inputMode: "tel" }}
            value={form.phone || ''}
            onChange={(e) => setForm({ ...form, phone: formatPhoneInput(e.target.value) })}
            fullWidth
            margin="dense"
            size="small"
          />
          <FormControl fullWidth margin="dense" size="small">
            <InputLabel>Role</InputLabel>
            <Select
              value={form.role || 'customer'}
              label="Role"
              onChange={(e) => setForm({ ...form, role: e.target.value as User['role'] })}
            >
              <MenuItem value="super_admin">Super Admin</MenuItem>
              <MenuItem value="location_admin">Location Admin</MenuItem>
              <MenuItem value="customer">Customer</MenuItem>
            </Select>
          </FormControl>

          <FormControlLabel
            control={
              <Checkbox
                checked={!!form.is_wholesale}
                onChange={(e) =>
                  setForm({
                    ...form,
                    is_wholesale: e.target.checked,
                    // Prefill the default tier when granting; clear it when revoking.
                    wholesale_tier_id: e.target.checked
                      ? (form.wholesale_tier_id ?? defaultTierId)
                      : null,
                  })
                }
              />
            }
            label="Wholesale customer (grants wholesale pricing access)"
            sx={{ mt: 1 }}
          />

          {form.is_wholesale && (
            <FormControl fullWidth margin="dense" size="small">
              <InputLabel>Wholesale tier</InputLabel>
              <Select
                value={form.wholesale_tier_id != null ? String(form.wholesale_tier_id) : ''}
                label="Wholesale tier"
                onChange={(e) =>
                  setForm({
                    ...form,
                    wholesale_tier_id: e.target.value ? Number(e.target.value) : null,
                  })
                }
              >
                {tiers.map((t) => (
                  <MenuItem key={t.id} value={String(t.id)}>
                    {t.name}
                    {t.discount_pct != null ? ` — ${Number(t.discount_pct)}% off` : ''}
                    {t.is_default ? ' (default)' : ''}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          <FormControl fullWidth margin="dense" size="small">
            <InputLabel>Location</InputLabel>
            <Select
              value={form.location_id?.toString() || ''}
              label="Location"
              onChange={(e) => setForm({
                ...form,
                location_id: e.target.value ? Number(e.target.value) : null
              })}
            >
              <MenuItem value="">No Location</MenuItem>
              {locations.map(location => (
                <MenuItem key={location.id} value={location.id.toString()}>
                  {location.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
            <Button
              variant="contained"
              fullWidth
              onClick={handleSubmit}
              disabled={
                !form.name ||
                !form.email ||
                // Creating a non-customer role requires a password.
                (!form.id && (form.role || 'customer') !== 'customer' && !form.password)
              }
              size="small"
            >
              Save
            </Button>
            <Button
              variant="outlined"
              fullWidth
              onClick={() => setOpenModal(false)}
              size="small"
            >
              Cancel
            </Button>
          </Box>
        </Box>
      </Modal>

      {/* Single Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>Are you sure you want to delete this user? This action cannot be undone.</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)} size="small">Cancel</Button>
          <Button
            color="error"
            variant="contained"
            onClick={() => userToDelete && handleDelete(userToDelete)}
            size="small"
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Bulk Delete Confirmation Dialog */}
      <Dialog open={bulkDeleteDialog} onClose={() => setBulkDeleteDialog(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Confirm Bulk Delete</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete {selectedUsers.length} users? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBulkDeleteDialog(false)} size="small">Cancel</Button>
          <Button
            color="error"
            variant="contained"
            onClick={handleBulkDelete}
            size="small"
          >
            Delete All
          </Button>
        </DialogActions>
      </Dialog>

    </Box>
  );
};

export default UsersPage;